import React, { useEffect, useMemo, useState } from 'react'
import Dropdown from '@/components/Dropdown'
import Button from '../Button'
import cls from 'classnames'
import { useLocale } from 'core/components/Locale'
import withClientComponent from 'core/components/withClientComponent'
import styles from './styles.module.scss'
import { useGetLiveRoomGetLiveRoomListQuery } from 'core/query/yapi/hk-web/LiveRoom/query'
import { useGetPublicServiceGetConfigQuery } from 'core/query/yapi/hk-web/AppConfig/query'
import { useParams } from 'next/navigation'
import { TLanguages } from 'core/config/locale'
import { useHCRouter } from '../HCNextLink'
import groovyWalkAnimation from '@/app/[lang]/live/room/data.json'
import dynamic from 'next/dynamic'
import { useAuthenticate } from 'core/components/Authenticate'
const Lottie = dynamic(() => import('lottie-react'), { ssr: false })

type TCustomerService = {
  children: React.ReactNode
}

const loadLiveChatScript = () => {
  window.__lc = window.__lc || {}
  window.__lc.license = 18379056
  window.__lc.integration_name = 'manual_channels'
  window.__lc.product_name = 'livechat'
  ;(function (n, t, c) {
    function i(n) {
      return e._h ? e._h.apply(null, n) : e._q.push(n)
    }
    var e = {
      _q: [],
      _h: null,
      _v: '2.0',
      on: function () {
        i(['on', c.call(arguments)])
      },
      once: function () {
        i(['once', c.call(arguments)])
      },
      off: function () {
        i(['off', c.call(arguments)])
      },
      get: function () {
        if (!e._h) throw new Error("[LiveChatWidget] You can't use getters before load.")
        return i(['get', c.call(arguments)])
      },
      call: function () {
        i(['call', c.call(arguments)])
      },
      init: function () {
        var n = t.createElement('script')
        ;(n.async = !0),
          (n.type = 'text/javascript'),
          (n.src = 'https://cdn.livechatinc.com/tracking.js'),
          t.head.appendChild(n)
      },
    }
    !n.__lc.asyncInit && e.init(), (n.LiveChatWidget = n.LiveChatWidget || e)
  })(window, document, [].slice)
}

const loadWebChatScript = (onload, retries = 3) => {
  // 客服 language用于控制访客端展示的语言类型，language=ZHCN为中文，language=EN为英文，您可按需设置一种语言类型
  const subCustomerServiceScript = document.createElement('script')
  subCustomerServiceScript.type = 'text/javascript'
  subCustomerServiceScript.src =
    'https://webchat.customerhc.com/javascripts/7moorInit.js?accessId=f86dc210-6f8c-11ec-a72f-8754094e991d&autoShow=true&language=EN'
  subCustomerServiceScript.onload = () => {
    subCustomerServiceScript.onerror = null
    if (typeof onload === 'function') onload()
  }
  subCustomerServiceScript.onerror = () => {
    console.log(`次线客服加载失败，剩余重试${retries - 1}`)
    subCustomerServiceScript.remove()
    if (retries > 1) {
      setTimeout(() => loadWebChatScript(onload, retries - 1), 2000)
    } else {
      console.log('次线客服无法加载')
    }
  }
  document.head.appendChild(subCustomerServiceScript)
}

let loadScriptTriggered = false
let subCustomerServiceScript: HTMLScriptElement | null = null
const CustomerService = (props: TCustomerService) => {
  const t = useLocale()
  const router = useHCRouter()
  const { loginData } = useAuthenticate()
  const language = useParams().lang as TLanguages
  const lang = language === 'zh_CN' || language === 'zh_HK' ? 'zh_CN' : 'en_US'
  const [subCustomerServiceReady, setSubCustomerServiceReady] = useState(false)

  const { data: configData } = useGetPublicServiceGetConfigQuery()

  const { data: livingdata, refetch: refetchLiving } = useGetLiveRoomGetLiveRoomListQuery(
    { status: 1 },
    { enabled: false, staleTime: 0 }
  )

  const liveRoom = useMemo(() => {
    if (!loginData) return null
    return livingdata?.data?.data?.find(
      (item) => item.lang === lang && item.status === 1 && item.roomType === 3 && item?.show?.[7] === '1'
    )
  }, [livingdata?.data?.data, lang, loginData])

  useEffect(() => {
    const loadScript = () => {
      // 如果已经触发过加载，直接返回
      if (loadScriptTriggered) {
        return
      }

      loadScriptTriggered = true
      loadLiveChatScript()

      // 检查是否已经存在脚本
      const existingScript = document.querySelector('script[src*="7moorInit.js"]')
      if (existingScript) {
        setSubCustomerServiceReady(true)
        return
      }
      loadWebChatScript(() => {
        setSubCustomerServiceReady(true)
      })
    }

    if (document.readyState === 'complete') {
      loadScript()
    } else {
      const handleLoad = () => {
        loadScript()
      }
      window.addEventListener('load', handleLoad)
      return () => {
        window.removeEventListener('load', handleLoad)
      }
    }
    return () => {
      document.removeEventListener('load', loadScript)
    }
  }, [])

  return (
    <Dropdown
      arrow={false}
      placement="topRight"
      text={props.children}
      onOpenChange={(open) => {
        if (open && !!loginData) {
          refetchLiving()
        }
      }}
    >
      <div className={styles.customerService}>
        <h3 className={styles.title}>{t('home_v101_WelcomeToHotcoin_1')}</h3>
        {configData?.data?.data?.serviceLines?.includes(2) && (
          <div className={styles.buttonItem}>
            <Button
              type="gray"
              onClick={() => {
                window.LiveChatWidget.call('maximize')
              }}
            >
              {t('home_v101_MainLineCustomerService')}
            </Button>
          </div>
        )}
        {configData?.data?.data?.serviceLines?.includes(1) && (
          <div className={styles.buttonItem}>
            <Button
              disabled={!subCustomerServiceReady}
              onClick={() => {
                window.qimoChatClick()
              }}
              type="gray"
            >
              {t('home_v101_SecondaryCustomerService')}
            </Button>
          </div>
        )}
        {liveRoom?.id && !!loginData && (
          <div className={cls(styles.buttonItem, styles.liveBtn)}>
            <Button
              onClick={() => {
                router.push(`/live/room?id=${liveRoom.id}`)
              }}
              type="gray"
            >
              {t('hotcoinWeb_v1_223423_LiveCustomerService')}
              <Lottie animationData={groovyWalkAnimation} loop={true} />
            </Button>
          </div>
        )}
      </div>
    </Dropdown>
  )
}

export default withClientComponent(CustomerService)
